// src/infrastructure/notifications/NotificationService.ts
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconInfoCircle
} from '@tabler/icons-react';
import type { INotificationService, NotificationOptions } from './NotificationService.types';
import { NOTIFICATION_CONFIG } from './notificationConfig';

/**
 * NotificationService - Centralized notification management
 *
 * Wraps Mantine's notification system with:
 * - Type-safe notification methods
 * - Consistent styling and behavior
 * - Icon management
 * - Auto-close configuration
 */
export class NotificationService implements INotificationService {
  /**
   * Show a success notification
   */
  showSuccess(message: string, title?: string, options?: Partial<NotificationOptions>): void {
    this.show({
      message,
      title: title ?? 'Success',
      type: 'success',
      icon: <IconCheck size={20} />,
      autoClose: NOTIFICATION_CONFIG.SUCCESS_DURATION,
      ...options
    });
  }

  /**
   * Show an error notification
   */
  showError(message: string, title?: string, options?: Partial<NotificationOptions>): void {
    this.show({
      message,
      title: title ?? 'Error',
      type: 'error',
      icon: <IconX size={20} />,
      autoClose: NOTIFICATION_CONFIG.ERROR_DURATION,
      ...options
    });
  }

  /**
   * Show a warning notification
   */
  showWarning(message: string, title?: string, options?: Partial<NotificationOptions>): void {
    this.show({
      message,
      title: title ?? 'Warning',
      type: 'warning',
      icon: <IconAlertTriangle size={20} />,
      autoClose: NOTIFICATION_CONFIG.AUTO_CLOSE_DURATION,
      ...options
    });
  }

  /**
   * Show an info notification
   */
  showInfo(message: string, title?: string, options?: Partial<NotificationOptions>): void {
    this.show({
      message,
      title: title ?? 'Info',
      type: 'info',
      icon: <IconInfoCircle size={20} />,
      autoClose: NOTIFICATION_CONFIG.AUTO_CLOSE_DURATION,
      ...options
    });
  }

  /**
   * Show a custom notification
   */
  show(options: NotificationOptions): void {
    const color = options.color ?? NOTIFICATION_CONFIG.COLORS[options.type];
    const withCloseButton = options.withCloseButton ?? NOTIFICATION_CONFIG.WITH_CLOSE_BUTTON;
    const autoClose = options.autoClose ?? NOTIFICATION_CONFIG.AUTO_CLOSE_DURATION;

    notifications.show({
      id: options.id,
      title: options.title,
      message: options.message,
      color,
      icon: options.icon,
      autoClose,
      withCloseButton,
      loading: options.loading,
      position: NOTIFICATION_CONFIG.POSITION,
      styles: {
        root: { maxWidth: 400 }
      }
    });
  }

  /**
   * Update an existing notification
   */
  update(id: string, options: Partial<NotificationOptions>): void {
    const color = options.color ?? (options.type ? NOTIFICATION_CONFIG.COLORS[options.type] : undefined);
    const withCloseButton = options.withCloseButton ?? NOTIFICATION_CONFIG.WITH_CLOSE_BUTTON;
    const autoClose = options.autoClose ?? NOTIFICATION_CONFIG.AUTO_CLOSE_DURATION;

    notifications.update({
      id,
      title: options.title,
      message: options.message ?? '',
      color,
      icon: options.icon,
      autoClose,
      withCloseButton,
      loading: options.loading,
      styles: {
        root: { maxWidth: 400 }
      }
    });
  }

  /**
   * Hide a specific notification
   */
  hide(id: string): void {
    notifications.hide(id);
  }

  /**
   * Hide all notifications
   */
  hideAll(): void {
    notifications.clean();
  }
}

/**
 * Singleton instance of NotificationService
 */
export const notificationService = new NotificationService();
